import { useTranslation } from 'react-i18next';
import { translateKickoffLabel } from '@/i18n/kickoff-label';
import { sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { translateLeagueBreadcrumb } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import kitGreenMd from '@/assets/kits/kit-green-md.svg';
import kitRedMd from '@/assets/kits/kit-red-md.svg';
import { Badge } from '../common/badge';
import { OddsButton } from '../common/odds-button';
import { SportIcon, type SportIconName } from '../common/icon';
import type { EventCompetitor, EventOutcome } from './EventRow';

export interface EventWidgetProps {
  /** Breadcrumb of category and tournament, e.g. `Brazil, Brasilero Serie A`. */
  league: string;
  /** Raw category name for SportsTranslates lookup. */
  category?: string;
  categoryId?: number;
  /** Raw tournament name for SportsTranslates lookup. */
  tournament?: string;
  tournamentId?: number;
  sportIcon: SportIconName;
  competitors: [EventCompetitor, EventCompetitor];
  /** Market shorthand shown above the odds, e.g. `1x2`. */
  marketCode: string;
  outcomes: EventOutcome[];
  type?: 'prematch' | 'live';
  state?: 'default' | 'suspended';
  /** Prematch kick-off label, e.g. `Today, 21:00`. */
  kickoff?: string;
  /** Live match clock, e.g. `67' · 2HT`. */
  clock?: string;
  onSelectOutcome?: (outcome: EventOutcome) => void;
  onOpenEvent?: () => void;
  className?: string;
}

const KIT_SRC = [kitRedMd, kitGreenMd] as const;

/**
 * Homepage featured event card (Figma EventWidget) — distinct from list {@link EventRow}.
 * Fixed width for horizontal Top Events slider; stretch via className on desktop if needed.
 */
export function EventWidget({
  league,
  category,
  categoryId,
  tournament,
  tournamentId,
  sportIcon,
  competitors,
  marketCode,
  outcomes,
  type = 'prematch',
  state = 'default',
  kickoff,
  clock,
  onSelectOutcome,
  onOpenEvent,
  className,
}: EventWidgetProps) {
  const { t, i18n } = useTranslation();
  const suspended = state === 'suspended';
  const isLive = type === 'live';
  const kickoffLabel = kickoff ? translateKickoffLabel(kickoff, t) : undefined;
  const versusLabel = t('versus', { defaultValue: 'versus' });
  const leagueLabel =
    category || tournament
      ? translateLeagueBreadcrumb(
          i18n,
          t,
          { category, categoryId, tournament, tournamentId },
          ', ',
        ) || league
      : league;

  return (
    <article
      className={cn(
        'relative flex w-[336px] shrink-0 flex-col gap-2 overflow-clip rounded-md border border-border-event bg-bg-surface p-3',
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 overflow-clip">
        <button
          type="button"
          onClick={onOpenEvent}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          <span className="flex size-5 shrink-0 items-center justify-center overflow-clip rounded-full">
            <SportIcon name={sportIcon} size={18} className="text-text-primary" />
          </span>
          <span className="truncate text-caption tracking-wide text-text-secondary">{leagueLabel}</span>
        </button>

        {isLive ? (
          <span className="flex shrink-0 items-center gap-1.5">
            <Badge tone="live">{t('Live', { defaultValue: 'Live' })}</Badge>
            {clock ? <span className="text-body-s text-text-secondary">{clock}</span> : null}
          </span>
        ) : kickoffLabel ? (
          <span className="shrink-0 text-label-s tracking-wide text-odds-default-text">
            {kickoffLabel}
          </span>
        ) : null}
      </header>

      <button
        type="button"
        onClick={onOpenEvent}
        className="flex flex-col gap-2 text-left"
        aria-label={`${competitors[0].name} ${versusLabel} ${competitors[1].name}`}
      >
        {competitors.map((competitor, index) => (
          <span key={`${competitor.name}-${index}`} className="flex items-center gap-2.5">
            <span className="relative size-8 shrink-0 overflow-clip">
              <img src={KIT_SRC[index]} alt="" className="size-full object-contain" />
            </span>
            <span className="min-w-0 flex-1 truncate text-body-m font-bold text-text-primary">
              {competitor.name}
            </span>
            <span className="flex size-9 shrink-0 items-center justify-center overflow-clip rounded-sm bg-bg-elevated">
              <span className="text-body-l font-bold tabular-nums text-text-primary">
                {competitor.score ?? '–'}
              </span>
            </span>
          </span>
        ))}
      </button>

      <p className="text-center text-caption tracking-wide text-text-secondary">{marketCode}</p>

      <div className="flex items-center gap-1">
        {outcomes.map((outcome) => (
          <OddsButton
            key={outcome.id}
            outcome={sanitizeOutcomeDisplay(outcome.outcome)}
            odds={outcome.odds}
            state={suspended ? 'locked' : (outcome.state ?? 'default')}
            flash={outcome.flash ?? 'none'}
            onClick={() => onSelectOutcome?.(outcome)}
          />
        ))}
      </div>
    </article>
  );
}
